/**
 * ContinuityCard
 * ------------------------------------------------------------------
 * Desktop admin ↔ mobile PWA credential handoff via QR code.
 *
 * Flow:
 *  1. Admin (already logged in on desktop) taps "Generate QR".
 *  2. We POST /api/continuity/create-handoff with the admin JWT.
 *  3. Server returns { token, expires_at, ttl_seconds }.
 *  4. We render a QR that encodes `${origin}/app?handoff=<token>`.
 *  5. User scans the QR on their phone, which opens /app?handoff=…
 *  6. Mobile app posts to /api/continuity/consume-handoff, flips
 *     owner mode on locally, and cleans the URL.
 *
 * Security:
 *  - Token TTL is 5 minutes (enforced server-side + Mongo TTL index).
 *  - Consumption is one-shot (find_and_delete).
 *  - Token is 24 URL-safe bytes (~192 bits of entropy).
 */

import React, { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, RefreshCw, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";

const authHeaders = () => {
  const t = typeof window !== "undefined" ? localStorage.getItem(AUTH_KEY) : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const ContinuityCard = () => {
  const [token, setToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(0);

  const url =
    token && typeof window !== "undefined"
      ? `${window.location.origin}/app?handoff=${encodeURIComponent(token)}`
      : null;

  // Countdown to expiry — refreshed every second.
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const secs = Math.max(
        0,
        Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000),
      );
      setRemaining(secs);
      if (secs <= 0) {
        setToken(null);
        setExpiresAt(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const generate = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/continuity/create-handoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setToken(j.token);
      setExpiresAt(j.expires_at);
    } catch (e) {
      setError(`Could not generate handoff: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const mm = String(Math.floor(remaining / 60)).padStart(1, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div
      data-testid="admin-continuity-card"
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: "rgba(0,229,255,0.08)", borderColor: "rgba(0,229,255,0.35)" }}
        >
          <Smartphone className="w-5 h-5" style={{ color: "#00E5FF" }} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-white text-base font-semibold leading-tight">
            Continuity — send owner mode to your phone
          </div>
          <p className="text-[12px] text-zinc-400 mt-1 leading-relaxed">
            Generate a QR, scan it with your phone&apos;s camera, and your
            mobile EVIQ opens with owner mode unlocked — no re-login needed.
            The QR is valid for 5 minutes and can only be used once.
          </p>
        </div>
      </div>

      {!token && (
        <button
          onClick={generate}
          disabled={loading}
          data-testid="admin-continuity-generate"
          className="mt-4 px-4 py-2.5 rounded-lg brand-gradient text-black font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={2.5} />
              Generating…
            </>
          ) : (
            <>
              <Smartphone className="w-4 h-4" strokeWidth={2.5} />
              Generate QR
            </>
          )}
        </button>
      )}

      {error && (
        <div className="mt-4 rounded-lg border border-[#FF6B57]/40 bg-[#FF6B57]/[0.06] p-3 text-[11px] text-[#FF6B57]">
          {error}
        </div>
      )}

      {token && url && (
        <div className="mt-4 flex flex-col sm:flex-row gap-4 items-start">
          <div
            className="bg-white p-3 rounded-lg shrink-0"
            data-testid="admin-continuity-qr"
            aria-label="Continuity handoff QR code"
          >
            <QRCodeSVG value={url} size={176} level="M" includeMargin={false} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              Scan with your phone camera
            </div>
            <div className="text-sm text-white break-all font-mono leading-snug">
              {url}
            </div>
            <div className="mt-3 flex items-center gap-2 text-[11px] font-mono">
              <Check className="w-3 h-3 text-[#39FF14]" strokeWidth={2.5} />
              <span className="text-zinc-400">
                Expires in{" "}
                <span className="text-white" data-testid="admin-continuity-countdown">
                  {mm}:{ss}
                </span>
              </span>
              <button
                onClick={generate}
                disabled={loading}
                data-testid="admin-continuity-regenerate"
                className="ml-auto text-zinc-400 hover:text-white inline-flex items-center gap-1"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
                New code
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
              After scanning, your phone opens EVIQ and your admin/owner mode
              is enabled automatically — no password, no re-login. If the
              code expires before you scan it, just tap &ldquo;New code&rdquo;.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
